import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import axios from 'axios';
import {
  AlertCircle,
  Banknote,
  Check,
  ChevronDown,
  Loader2,
  UserMinus,
  X,
} from 'lucide-react';

const RETIREMENT_TYPE_OPTIONS = [
  { value: 'superannuation', label: 'Superannuation' },
  { value: 'voluntary_retirement', label: 'Voluntary Retirement' },
  { value: 'medical_retirement', label: 'Medical Retirement' },
  { value: 'compulsory_retirement', label: 'Compulsory Retirement' },
  { value: 'death_in_service', label: 'Death in Service' },
  { value: 'absconding', label: 'Absconding' },
  { value: 'other', label: 'Other' },
];

const DATE_LABEL_MAP = {
  'superannuation': 'Date of Retirement',
  'voluntary_retirement': 'Date of Retirement',
  'medical_retirement': 'Date of Retirement',
  'compulsory_retirement': 'Date of Retirement',
  'death_in_service': 'Date of Death',
  'absconding': 'Date of Absconding',
  'other': 'Date of Retirement',
};

const initialFormState = (employee = null) => ({
  retirement_type: employee?.retirement_type || '',
  retirement_date: employee?.retirement_date || '',
});

const SectionCard = ({ title, icon: Icon, children, accent = 'bg-blue-600' }) => (
  <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
    <div className={`flex items-center gap-2 px-4 py-3 text-white ${accent}`}>
      <Icon className="h-4 w-4" />
      <h4 className="text-sm font-semibold tracking-wide">{title}</h4>
    </div>
    <div className="p-4 sm:p-5">{children}</div>
  </div>
);

const FieldLabel = ({ children, required }) => (
  <label className="block text-sm font-medium text-gray-700">
    {children}{required && <span className="text-red-500"> *</span>}
  </label>
);

const FieldShell = ({ error, children }) => (
  <div className="space-y-1">
    {children}
    {error ? (
      <p className="flex items-center gap-1 text-xs text-red-600">
        <AlertCircle className="h-3.5 w-3.5" />
        {error}
      </p>
    ) : null}
  </div>
);

const Toast = ({ toast, onClose }) => (
  <motion.div
    initial={{ opacity: 0, x: 80, scale: 0.96 }}
    animate={{ opacity: 1, x: 0, scale: 1 }}
    exit={{ opacity: 0, x: 80, scale: 0.96 }}
    className={`max-w-md rounded-lg border bg-white p-4 shadow-lg ${toast.type === 'error' ? 'border-red-200' : 'border-green-200'}`}
  >
    <div className="flex items-start gap-3">
      {toast.type === 'error' ? (
        <AlertCircle className="mt-0.5 h-5 w-5 text-red-600" />
      ) : (
        <Check className="mt-0.5 h-5 w-5 text-green-600" />
      )}
      <div className="flex-1">
        <p className="font-semibold text-gray-900">{toast.title}</p>
        <p className="text-sm text-gray-600">{toast.message}</p>
      </div>
      <button type="button" onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700" aria-label="Dismiss">
        <X className="h-4 w-4" />
      </button>
    </div>
  </motion.div>
);

export default function RetireEmployeeModal({ isOpen, onClose, employee, onSuccess, separationType }) {
  const [formData, setFormData] = useState(() => initialFormState(employee));
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const employeeLabel = useMemo(() => {
    if (!employee) return '';
    return employee.emp_name || employee.full_name || `${employee.first_name || ''} ${employee.last_name || ''}`.trim() || employee.emp_code || `Employee #${employee.id}`;
  }, [employee]);

  const isDeath = separationType === 'death' || formData.retirement_type === 'death_in_service';
  const isSuperannuation = separationType === 'superannuation' || formData.retirement_type === 'superannuation';
  const isVoluntary = separationType === 'voluntary' || formData.retirement_type === 'voluntary_retirement';
  const isAbsconding = separationType === 'absconding' || formData.retirement_type === 'absconding';

  let modalTitle = "Retire Employee";
  let modalSubtitle = (
    <p className="text-sm text-gray-500">
      Convert <span className="font-medium text-gray-700">{employeeLabel}</span> into a pensioner profile.
    </p>
  );
  let dateLabel = DATE_LABEL_MAP[formData.retirement_type] || "Retirement Date";
  let sectionTitle = "Retirement Details";

  if (isDeath) {
    modalTitle = "Declare Employee Deceased";
    modalSubtitle = (
      <p className="text-sm text-gray-500">
        Record death in service for <span className="font-medium text-gray-700">{employeeLabel}</span>.
      </p>
    );
    sectionTitle = "Separation Details";
  } else if (isSuperannuation) {
    modalTitle = "Superannuate Employee";
    modalSubtitle = (
      <p className="text-sm text-gray-500">
        Record superannuation for <span className="font-medium text-gray-700">{employeeLabel}</span>.
      </p>
    );
    sectionTitle = "Superannuation Details";
  } else if (isVoluntary) {
    modalTitle = "Voluntary Retirement";
    modalSubtitle = (
      <p className="text-sm text-gray-500">
        Record voluntary retirement for <span className="font-medium text-gray-700">{employeeLabel}</span>.
      </p>
    );
    sectionTitle = "Voluntary Retirement Details";
  } else if (isAbsconding) {
    modalTitle = "Mark Employee Absconding";
    modalSubtitle = (
      <p className="text-sm text-gray-500">
        Record absconding for <span className="font-medium text-gray-700">{employeeLabel}</span>.
      </p>
    );
    sectionTitle = "Absconding Details";
  }

  useEffect(() => {
    if (isOpen) {
      setFormData(initialFormState(employee));
      setErrors({});
      setLoading(false);
      setToast(null);
      // Pre-select retirement type based on separationType
      if (separationType) {
        const typeMapping = {
          'retirement': 'superannuation',
          'death': 'death_in_service',
          'superannuation': 'superannuation',
          'voluntary': 'voluntary_retirement',
          'absconding': 'absconding'
        };
        setFormData(prev => ({ ...prev, retirement_type: typeMapping[separationType] || '' }));
      }
    }
  }, [isOpen, employee, separationType]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(null), 2500);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const next = {};
    if (!formData.retirement_type) next.retirement_type = 'Retirement type is required';
    if (!formData.retirement_date) next.retirement_date = `${dateLabel} is required`;
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!validate()) {
      setToast({
        type: 'error',
        title: 'Please fix the form',
        message: 'One or more required fields are missing or invalid.',
      });
      return;
    }

    setLoading(true);
    setToast(null);

    try {
      const payload = {
        retirement_type: formData.retirement_type,
        retirement_date: formData.retirement_date,
      };

      const response = await axios.post(
        `/v1/pensioners/retire/${employee.id}`,
        payload,
        { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } }
      );

      let successTitle = 'Employee retired successfully';
      let successMessage = `${employeeLabel} has been converted to a pensioner. Pension details can be completed later.`;
      if (isDeath) {
        successTitle = 'Employee declared deceased';
        successMessage = `${employeeLabel} has been updated to deceased status. Pension details can be completed later.`;
      } else if (isSuperannuation) {
        successTitle = 'Employee superannuated successfully';
        successMessage = `${employeeLabel} has been superannuated. Pension details can be completed later.`;
      } else if (isVoluntary) {
        successTitle = 'Voluntary retirement registered';
        successMessage = `${employeeLabel} has been voluntarily retired. Pension details can be completed later.`;
      } else if (isAbsconding) {
        successTitle = 'Employee marked absconding';
        successMessage = `${employeeLabel} has been marked as absconding. Pension details can be completed later.`;
      }

      setToast({
        type: 'success',
        title: successTitle,
        message: successMessage,
      });

      onSuccess?.(response.data);
      window.setTimeout(() => {
        onClose?.();
      }, 900);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = typeof detail === 'string'
        ? detail
        : 'Failed to complete separation process. Please try again.';
      setToast({
        type: 'error',
        title: 'Action failed',
        message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && employee && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center px-4 py-8">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-slate-900/50 backdrop-blur-[2px]"
              onClick={loading ? undefined : onClose}
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 18 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.98, y: 18 }}
              transition={{ duration: 0.22, ease: 'easeOut' }}
              className="relative z-10 w-full max-w-4xl overflow-hidden rounded-2xl border border-gray-200 bg-gray-50 shadow-2xl"
            >
              <div className="flex items-start justify-between border-b border-gray-200 bg-white px-5 py-4 sm:px-6">
                <div>
                  <div className="flex items-center gap-2">
                    <div className="rounded-lg bg-red-100 p-2 text-red-600">
                      <UserMinus className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{modalTitle}</h3>
                      {modalSubtitle}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={loading ? undefined : onClose}
                  className="rounded-full p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={loading}
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="max-h-[80vh] overflow-y-auto px-5 py-5 sm:px-6">
                <div className="grid gap-5">
                  <SectionCard title={sectionTitle} icon={Banknote} accent="bg-slate-700">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <FieldShell error={errors.retirement_type}>
                        <FieldLabel required>Retirement Type</FieldLabel>
                        <div className="relative">
                          <select
                            value={formData.retirement_type}
                            onChange={(e) => handleChange('retirement_type', e.target.value)}
                            className={`w-full appearance-none rounded-md border px-3 py-2.5 text-sm outline-none transition focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.retirement_type ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'}`}
                          >
                            <option value="">Select retirement type</option>
                            {RETIREMENT_TYPE_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                        </div>
                      </FieldShell>

                      <FieldShell error={errors.retirement_date}>
                        <FieldLabel required>{dateLabel}</FieldLabel>
                        <input
                          type="date"
                          value={formData.retirement_date}
                          onChange={(e) => handleChange('retirement_date', e.target.value)}
                          className={`w-full rounded-md border px-3 py-2.5 text-sm outline-none transition focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.retirement_date ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'}`}
                        />
                      </FieldShell>
                    </div>
                  </SectionCard>
                </div>

                <div className="mt-5 flex flex-col-reverse gap-3 border-t border-gray-200 bg-white px-0 pt-5 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-xs text-gray-500">
                    Pension and bank details can be completed later from the Pensioner dashboard.
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={loading ? undefined : onClose}
                      disabled={loading}
                      className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="inline-flex min-w-36 items-center justify-center rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {loading ? (
                        <span className="inline-flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Retiring...
                        </span>
                      ) : (
                        'Retire Employee'
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </motion.div>
          </div>

          <div className="pointer-events-none fixed right-4 top-4 z-[60] space-y-3">
            <AnimatePresence>
              {toast && <Toast toast={toast} onClose={() => setToast(null)} />}
            </AnimatePresence>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
}
